import { z } from 'zod';
import { create } from 'zustand';

import { getDb, META_KEYS, readMeta, writeMeta } from '../offline/db';

/**
 * Rollen, zoals de API ze noemt. Code-identifiers zijn Engels, de labels zijn
 * Nederlands en staan in `docs/woordenlijst.md` (gelijk aan `Role::label()` in
 * de API).
 *
 * Er zijn er precies drie: de eigenaar van een keuringsbedrijf, de inspecteur en
 * de platformbeheerder. Iemand kan eigenaar én inspecteur zijn.
 */
export const ROLES = ['inspector', 'owner', 'platform_admin'] as const;

export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
    inspector: 'Inspecteur',
    owner: 'Eigenaar',
    platform_admin: 'Platformbeheerder',
};

export function isRole(value: string): value is Role {
    return (ROLES as readonly string[]).includes(value);
}

/**
 * De sessie
 * =========
 * Eén vorm voor de hele app, in camelCase. Ze komt uit precies één mapping in
 * `entities/auth` (van het antwoord van login, switch-tenant, refresh en een
 * geaccepteerde uitnodiging, of van `GET /auth/me`) en nergens anders vandaan.
 *
 * Andere delen van de app bouwen hierop - de offline opslag per bedrijf leest
 * `tenant.uuid` en `device.uuid`. Verander deze namen dus niet.
 */

/**
 * Een keuringsbedrijf. `accentColor` is de huisstijlkleur van dat bedrijf: die
 * kleurt het rapport en de klantpagina, nooit de app zelf.
 */
export interface SessionTenant {
    uuid: string;
    name: string;
    slug: string;
    logoUrl: string | null;
    accentColor: string | null;
}

/** Een omgeving waar deze persoon actief lid van is, met zijn rollen daar. */
export interface SessionMembership {
    tenant: SessionTenant;
    roles: Role[];
    isDefault: boolean;
}

export interface SessionUser {
    uuid: string;
    name: string;
    email: string;
    isPlatformAdmin: boolean;
    /**
     * Wanneer de rondleiding bij het eerste bezoek is afgemaakt of overgeslagen.
     * `null`: hij moet nog komen. Ontbreekt het veld (een sessie van vóór de
     * rondleiding), dan weten we het niet en komt hij niet: wie toen al een
     * account had, heeft hem op de server als afgerond staan.
     */
    tourCompletedAt?: string | null;
}

export interface SessionDevice {
    /** De ULID waarmee dit apparaat voor deze persoon bij de API bekendstaat. */
    uuid: string;
}

export interface Session {
    token: string;
    /** ISO-8601; `null` alleen als het token niet verloopt. */
    expiresAt: string | null;
    device: SessionDevice;
    user: SessionUser;
    /** De omgeving waarin dit token werkt. `null` voor een platformbeheerder zonder omgeving. */
    tenant: SessionTenant | null;
    memberships: SessionMembership[];
    /** Rollen in `tenant` (of platformrollen als er geen omgeving is). */
    roles: Role[];
    permissions: string[];
}

const tenantSchema = z.object({
    uuid: z.string().min(1),
    name: z.string(),
    slug: z.string(),
    logoUrl: z.string().nullable(),
    accentColor: z.string().nullable(),
});

/**
 * Rollen in een bewaarde sessie. Een rolcode die de app niet (meer) kent, valt
 * weg in plaats van de hele sessie af te keuren: na een update die rollen
 * opheft kan een tablet zonder netwerk opstarten met een sessie van daarvoor.
 * De mapping van de API (`entities/auth`) doet hetzelfde, en `GET /auth/me`
 * zet de rollen bij de eerstvolgende verbinding weer gelijk met de API.
 */
const storedRoles = z.array(z.string()).transform((names) => names.filter(isRole));

/**
 * Controleert een bewaarde sessie bij het opstarten. Een sessie in een oudere
 * vorm (of een verzonnen sessie uit de tijd zonder API) wordt niet gebruikt:
 * dan volgt gewoon het inlogscherm.
 */
export const sessionSchema: z.ZodType<Session> = z.object({
    token: z.string().min(1),
    expiresAt: z.string().nullable(),
    device: z.object({ uuid: z.string().min(1) }),
    user: z.object({
        uuid: z.string().min(1),
        name: z.string(),
        email: z.string(),
        isPlatformAdmin: z.boolean(),
        tourCompletedAt: z.string().nullable().optional(),
    }),
    tenant: tenantSchema.nullable(),
    memberships: z.array(
        z.object({
            tenant: tenantSchema,
            roles: storedRoles,
            isDefault: z.boolean(),
        }),
    ),
    roles: storedRoles,
    permissions: z.array(z.string()),
});

/**
 * Meekijken als iemand anders.
 *
 * De platformbeheerder krijgt een tijdelijk token voor één tenant. Zolang dat
 * er is gaat *dat* token mee met elk verzoek, samen met de tenant waar het bij
 * hoort; stoppen zet het eigen token terug. Wie er meekijkt (`session.user`)
 * verandert niet - dat is precies wat de audit log moet blijven zien.
 *
 * Het token staat bewust alleen in het geheugen. De zichtbare status wordt in
 * `sessionStorage` bewaard door `features/admin/impersonation`; de schil spiegelt
 * die status na een herlaad terug naar deze store.
 */
export interface Impersonation {
    token: string;
    tenantId: string;
    tenantName: string;
    userName: string;
    /** ISO-8601 of null wanneer het token niet verloopt. */
    expiresAt: string | null;
}

/**
 * Waarom een sessie eindigde. Het inlogscherm legt het uit, zodat iemand die
 * midden in zijn werk wordt uitgelogd weet dat hij niets fout deed.
 */
export type SessionEndReason = 'signed-out' | 'expired' | 'device-unavailable';

/**
 * Tokenopslag
 * ===========
 * Het token leeft in het geheugen (deze store) en wordt daarnaast in IndexedDB
 * bewaard, want de app moet na een herstart in een kruipruimte - zonder
 * netwerk - nog weten wie is ingelogd. Bewust *niet* in `localStorage`: dat is
 * synchroon leesbaar vanuit elk script op de pagina en wordt vaker per ongeluk
 * gelogd. Een httpOnly-cookie kan niet, omdat de API op een ander domein staat.
 */
interface SessionState {
    session: Session | null;
    impersonation: Impersonation | null;
    hydrated: boolean;
    endedBecause: SessionEndReason | null;
    /** Zet een nieuwe of vernieuwde sessie en bewaart haar. */
    signIn: (session: Session) => Promise<void>;
    /** Beëindigt de sessie op dit apparaat. Het token intrekken doet `entities/auth`. */
    signOut: (reason?: SessionEndReason) => Promise<void>;
    hydrate: () => Promise<Session | null>;
    /** `null` stopt het meekijken en zet het eigen token terug. */
    setImpersonation: (impersonation: Impersonation | null) => void;
}

export const useSessionStore = create<SessionState>((set) => ({
    session: null,
    impersonation: null,
    hydrated: false,
    endedBecause: null,

    async signIn(session) {
        set({ session, hydrated: true, endedBecause: null });
        await writeMeta(getDb(), META_KEYS.session, session);
    },

    async signOut(reason = 'signed-out') {
        set({ session: null, impersonation: null, hydrated: true, endedBecause: reason });
        await writeMeta(getDb(), META_KEYS.session, null);
    },

    async hydrate() {
        const stored = await readMeta<unknown>(getDb(), META_KEYS.session);
        const parsed = sessionSchema.safeParse(stored);
        const session = parsed.success && isUsable(parsed.data) ? parsed.data : null;

        set({ session, hydrated: true });

        return session;
    },

    setImpersonation(impersonation) {
        set({ impersonation });
    },
}));

/**
 * Een verlopen token is online het einde van de sessie. Offline niet: de
 * inspecteur in de kruipruimte werkt door op zijn lokale kopie, en zodra er
 * weer verbinding is vraagt de app `GET /auth/me` - dan volgt pas het
 * inlogscherm.
 */
function isUsable(session: Session): boolean {
    if (!session.expiresAt || Date.parse(session.expiresAt) > Date.now()) {
        return true;
    }

    return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * Tokens die op het punt staan vervangen te worden.
 *
 * Wisselen van omgeving en verlengen trekken het vorige token van dit apparaat
 * meteen in. Een verzoek dat nog met dat oude token onderweg was, komt terug
 * met een 401 - dat is geen verlopen sessie en mag niemand uitloggen.
 */
const retiring = new Set<string>();

/** Markeert een token als "wordt vervangen". Geeft een functie die dat terugdraait. */
export function retireToken(token: string): () => void {
    retiring.add(token);

    return () => {
        retiring.delete(token);
    };
}

/** Zegt een 401 op dit token iets over de sessie van nu? */
export function isCurrentToken(token: string): boolean {
    return !retiring.has(token) && useSessionStore.getState().session?.token === token;
}

/**
 * Leest het token buiten React om, voor de http-client. Loopt er een
 * impersonatie, dan is dat tijdelijke token het token dat meegaat.
 */
export function currentToken(): string | null {
    const { session, impersonation } = useSessionStore.getState();

    return impersonation?.token ?? session?.token ?? null;
}

/** Idem voor `X-Tenant`: meekijken gebeurt altijd in één tenant. */
export function currentTenantId(): string | null {
    const { session, impersonation } = useSessionStore.getState();

    return impersonation?.tenantId ?? session?.tenant?.uuid ?? null;
}

/**
 * De ingelogde gebruiker - ook tijdens het meekijken. De beheerder blijft de
 * handelende persoon; de naam van degene op wie wordt meegekeken staat in
 * `impersonation.userName`.
 */
export function currentUser(): SessionUser | null {
    return useSessionStore.getState().session?.user ?? null;
}

export function currentImpersonation(): Impersonation | null {
    return useSessionStore.getState().impersonation;
}

/** Wat een toegangsvraag nodig heeft. */
export type SessionAccess = Pick<Session, 'user' | 'roles'>;

/**
 * Heeft deze sessie een van deze rollen in de huidige omgeving? Een
 * platformbeheerder komt overal binnen.
 */
export function hasRole(session: SessionAccess | null, roles: readonly Role[]): boolean {
    if (!session) {
        return false;
    }

    if (isPlatformAdmin(session)) {
        return true;
    }

    return roles.some((role) => session.roles.includes(role));
}

/**
 * Is dit écht een platformbeheerder? `hasRole` laat hem overal binnen, dus deze
 * vraag staat apart - de platformgroep in de rail is alleen voor hem.
 */
export function isPlatformAdmin(session: SessionAccess | null): boolean {
    return (
        session?.user.isPlatformAdmin === true || session?.roles.includes('platform_admin') === true
    );
}

/**
 * Kan deze sessie de schermen van een keuringsbedrijf openen? Daarvoor is een
 * omgeving nodig: die van het token, of die waarin een beheerder meekijkt.
 */
export function hasWorkspace(
    session: Pick<Session, 'tenant'> | null,
    impersonation: Impersonation | null,
): boolean {
    return session !== null && (session.tenant !== null || impersonation !== null);
}
